//src/utils/signingPermissions.ts

import type { Account } from '@/types/Account'
import { Roles } from '@/types/Helper/Enums'
import type { TrainingPart } from '@/types/Part141/Part141Course'
import { MaintEndorsement, MaintRecord } from '@/types/Helper/MaintenanceEnums'

// ── Who may sign — training records and maintenance records ─────────────────
//
// The ONLY place these role lists live (the screens use them; firestore.rules
// mirrors them). Uses exact role checks (hasRole), NOT hasRoles() — hasRoles()
// passes every Owner and Dev regardless of the roles asked about. Dev is
// allowed for support.

/** Holds one of these roles at this school — no Dev exception. */
function holdsAnyRole(
  user: Account | null | undefined,
  roles: readonly Roles[],
  storeCode: string
): boolean {
  return !!user && roles.some((role) => user.hasRole(role, storeCode))
}

function hasAnyExactRole(
  user: Account | null | undefined,
  roles: readonly Roles[],
  storeCode: string
): boolean {
  if (!user) return false
  if (user.isDev()) return true
  return holdsAnyRole(user, roles, storeCode)
}

// ════════════════════════════════════════════════════════════════════════════
// TRAINING (Part 141 / 61 gradesheets)
// ════════════════════════════════════════════════════════════════════════════
//
//   Part 61  — any instructor role may grade new gradesheets. The "Part 61"
//              role is only a visual label; it isn't required.
//   Part 141 — anything that creates or changes a Part 141 record (grading,
//              signing, changing saved grades, delete/restore) ALSO requires
//              the "Part 141" role at the school.
//   Seeing   — any instructor role may open the training screens (Part 141
//              included); what they may change follows the rules above.
// Roles.Instructor is shown as "Instructor - Affiliated".

/** Any instructor — may open the training screens and grade NEW Part 61 gradesheets. */
export const INSTRUCTOR_ROLES: readonly Roles[] = [
  Roles.CFI,
  Roles.CFII,
  Roles.MEI,
  Roles.Instructor,
  Roles.chiefInstructor,
  Roles.ChiefPilot,
  Roles.asstchiefPilot
]

/** May grade, sign and save a NEW gradesheet (plus Part 141 for Part 141). */
export const NEW_GRADE_ROLES: readonly Roles[] = INSTRUCTOR_ROLES

/** May change grades on a gradesheet that's already saved (plus Part 141 for Part 141). */
export const CHANGE_GRADE_ROLES: readonly Roles[] = [
  Roles.Instructor,
  Roles.chiefInstructor,
  Roles.ChiefPilot,
  Roles.asstchiefPilot
]

/** Required, in addition, for any change to a Part 141 record. */
export const PART_141_ROLE = Roles.Part141

export const CHANGE_GRADES_MESSAGE =
  'Saved grades can only be changed by an Affiliated Instructor, Chief Instructor, Chief Pilot or Assistant Chief Pilot.'

export const PART_141_MESSAGE =
  'Part 141 records can only be changed by staff who hold the Part 141 role at this school.'

/** Part 141 work needs the Part 141 role at this school; Part 61 doesn't. */
export function hasProgramAccess(
  user: Account | null | undefined,
  storeCode: string,
  program: TrainingPart
): boolean {
  if (program !== '141') return true
  return hasAnyExactRole(user, [PART_141_ROLE], storeCode)
}

/** Any instructor role — may open the training screens. */
export function isInstructor(user: Account | null | undefined, storeCode: string): boolean {
  return hasAnyExactRole(user, INSTRUCTOR_ROLES, storeCode)
}

export function canGradeNewSession(
  user: Account | null | undefined,
  storeCode: string,
  program: TrainingPart
): boolean {
  return (
    hasAnyExactRole(user, NEW_GRADE_ROLES, storeCode) && hasProgramAccess(user, storeCode, program)
  )
}

export function canChangeSavedGrades(
  user: Account | null | undefined,
  storeCode: string,
  program: TrainingPart
): boolean {
  return (
    hasAnyExactRole(user, CHANGE_GRADE_ROLES, storeCode) &&
    hasProgramAccess(user, storeCode, program)
  )
}

/** Why a change isn't allowed, for the message shown to the user. */
export function changeSavedGradesMessage(
  user: Account | null | undefined,
  storeCode: string,
  program: TrainingPart
): string {
  return hasProgramAccess(user, storeCode, program) ? CHANGE_GRADES_MESSAGE : PART_141_MESSAGE
}

// ════════════════════════════════════════════════════════════════════════════
// PART 141 CERTIFICATES (enrollment §141.93, graduation §141.95, and the
// record certification on the course record)
// ════════════════════════════════════════════════════════════════════════════
//
//   Sign   — Chief Instructor only (§§ 141.93 and 141.95 call for the chief
//            instructor's signature), so the "Chief Instructor" caption on
//            the printed certificate is always right.
//   Remove — Chief Pilot or Assistant Chief Pilot, confirming with their own
//            password while signed in, so the change history records them.
//   Both also need the Part 141 role. No Dev exception: the certificate
//   carries the signer's own name and signature.

export const CERTIFICATE_SIGNER_ROLES: readonly Roles[] = [Roles.chiefInstructor]

export const CERTIFICATE_REMOVE_ROLES: readonly Roles[] = [Roles.ChiefPilot, Roles.asstchiefPilot]

export const CERTIFICATE_SIGNER_MESSAGE =
  'Certificates can only be signed by the Chief Instructor, who must also hold the Part 141 role at this school.'

export const CERTIFICATE_REMOVE_MESSAGE =
  'Only a Chief Pilot or Assistant Chief Pilot who holds the Part 141 role at this school can remove a certificate signature.'

export function canSignCertificate(user: Account | null | undefined, storeCode: string): boolean {
  return (
    holdsAnyRole(user, CERTIFICATE_SIGNER_ROLES, storeCode) &&
    holdsAnyRole(user, [PART_141_ROLE], storeCode)
  )
}

export function canRemoveCertificateSignature(
  user: Account | null | undefined,
  storeCode: string
): boolean {
  return (
    holdsAnyRole(user, CERTIFICATE_REMOVE_ROLES, storeCode) &&
    holdsAnyRole(user, [PART_141_ROLE], storeCode)
  )
}

// ════════════════════════════════════════════════════════════════════════════
// MAINTENANCE (aircraft maintenance entries)
// ════════════════════════════════════════════════════════════════════════════
//
//   Create / edit an UNSIGNED entry — any maintenance role (Maintenance Tech
//     or any mechanic).
//   Maintenance setup (AD/CAI items, inspection templates) — Maintenance Tech.
//   Sign (or void) an entry — a mechanic role matching the entry:
//     Annual                       → IA
//     Engine or Propeller entry    → Mechanic-P, Mechanic-AP or IA
//     Airframe or "Other" entry    → Mechanic-A, Mechanic-AP or IA
//   A signed entry is never edited in place — only corrected (void + a new
//   signed entry).
// The mechanic's credentials on a signed entry ("1234567 (AP)") come from
// these roles too — one source of truth.

/** May manage maintenance setup: AD/CAI items and inspection templates. */
export const MAINTENANCE_SETUP_ROLES: readonly Roles[] = [Roles.Maintenance]

/**
 * May edit an aircraft's maintenance-related cards — Status, Equipment,
 * Documents, Engines, Propellers: the school's Owner or Admin, or the
 * Maintenance role (Dev for support). Requirements, Info, Owner and Admin
 * stay Owner/Admin only. firestore.rules mirror this.
 */
export function canEditAircraftMaintenance(
  user: Account | null | undefined,
  storeCode: string
): boolean {
  return hasAnyExactRole(user, [Roles.StoreOwner, Roles.Admin, Roles.Maintenance], storeCode)
}

/** Every mechanic role (any of them may sign SOMETHING). */
export const MECHANIC_ROLES: readonly Roles[] = [
  Roles.MechanicA,
  Roles.MechanicP,
  Roles.MechanicAP,
  Roles.IA
]

/**
 * Everyone who works on maintenance: Maintenance Tech (drafts, AD/CAI items)
 * and the mechanics (signing). Opens the Maintenance menu and its pages
 * (sidebar and router); what each may DO there is decided by the checks above.
 */
export const MAINTENANCE_STAFF_ROLES: readonly Roles[] = [Roles.Maintenance, ...MECHANIC_ROLES]

/** The mechanic roles that may sign an entry of this category / endorsement. */
export function signerRolesFor(
  category: MaintRecord,
  endorsement: MaintEndorsement | null
): readonly Roles[] {
  if (endorsement === MaintEndorsement.annual) return [Roles.IA]
  if (category === MaintRecord.Engine || category === MaintRecord.Propeller) {
    return [Roles.MechanicP, Roles.MechanicAP, Roles.IA]
  }
  return [Roles.MechanicA, Roles.MechanicAP, Roles.IA]
}

/** Plain words for who may sign it, e.g. "an IA". */
export function signerRolesLabel(
  category: MaintRecord,
  endorsement: MaintEndorsement | null
): string {
  if (endorsement === MaintEndorsement.annual) return 'an IA'
  if (category === MaintRecord.Engine || category === MaintRecord.Propeller) {
    return 'a Powerplant mechanic (Mechanic-P or Mechanic-AP) or an IA'
  }
  return 'an Airframe mechanic (Mechanic-A or Mechanic-AP) or an IA'
}

/**
 * May create and edit UNSIGNED maintenance entries: any maintenance role —
 * Maintenance Tech or any mechanic. (Signing is canSignMaintenance: the
 * qualified mechanic only.) firestore.rules mirror this.
 */
export function canEditMaintenance(user: Account | null | undefined, storeCode: string): boolean {
  return hasAnyExactRole(user, MAINTENANCE_STAFF_ROLES, storeCode)
}

/**
 * May manage maintenance SETUP — AD/CAI items and inspection endorsement
 * templates: Maintenance Tech only (templates also Admin, by their own route).
 * Mechanics can't open these editors. firestore.rules mirror this.
 */
export function canManageMaintenanceSetup(
  user: Account | null | undefined,
  storeCode: string
): boolean {
  return hasAnyExactRole(user, MAINTENANCE_SETUP_ROLES, storeCode)
}

/** May sign — or void — an entry of this category / endorsement. */
export function canSignMaintenance(
  user: Account | null | undefined,
  storeCode: string,
  category: MaintRecord,
  endorsement: MaintEndorsement | null
): boolean {
  return hasAnyExactRole(user, signerRolesFor(category, endorsement), storeCode)
}

/** "IA", "AP", "A" or "P" from the account's mechanic roles at this school ('' if none). */
export function mechanicSuffix(user: Account | null | undefined, storeCode: string): string {
  if (!user) return ''
  const has = (role: Roles) => user.hasRole(role, storeCode)
  if (has(Roles.IA)) return 'IA'
  const a = has(Roles.MechanicA) || has(Roles.MechanicAP)
  const p = has(Roles.MechanicP) || has(Roles.MechanicAP)
  return a && p ? 'AP' : a ? 'A' : p ? 'P' : ''
}

/** "1234567 (AP)" — the certificate number from the mechanic profile plus the suffix. */
export function mechanicCredentials(user: Account | null | undefined, storeCode: string): string {
  const suffix = mechanicSuffix(user, storeCode)
  if (!suffix) return ''
  const number = user?.mechanic?.certificateNumber?.trim() ?? ''
  return number ? `${number} (${suffix})` : `(${suffix})`
}

// ════════════════════════════════════════════════════════════════════════════
// PARTS INVENTORY (src/utils/maintenance/inventoryData.ts)
// ════════════════════════════════════════════════════════════════════════════
//
//   Any maintenance role — the catalog, receiving, moving, counting,
//                          quarantining, marking unserviceable.
//   A mechanic, signing  — the receiving inspection, release from quarantine,
//                          scrapping (certificated persons; Part 145 §145.211).
//   Owner / Admin / Finance may also READ it (firestore.rules) — costs.

/** May work the inventory: catalog, receiving, moves, counts, quarantine. */
export function canUseInventory(user: Account | null | undefined, storeCode: string): boolean {
  return hasAnyExactRole(user, MAINTENANCE_STAFF_ROLES, storeCode)
}

/** May make (and sign) the inspection decisions: accept, release, scrap. */
export function canInspectInventory(user: Account | null | undefined, storeCode: string): boolean {
  return hasAnyExactRole(user, MECHANIC_ROLES, storeCode)
}
